// Hábitos do Diário (0.65): dias planejados, dias cumpridos (marcados à mão, pelo Bem-Estar ou pela tarefa enviada
// ao My Planner) e a sequência ("🔥 N dias seguidos" ou "N dias cumpridos no mês").
import type { Docs } from '../store/types';
import type { FreqHabito, Habito, LigacaoBE, MetaVida } from '../store/diario';
import { addDays, parseYmd, today } from './date';
import { totalAgua, doDia, refeicoesEmOrdem } from '../store/bemestar';
import { N_, t } from './i18n';
import { SEMANA_CURTA } from './date';

export const LIGACOES_BE: [LigacaoBE, string][] = [['agua', N_('Meta de água do Bem-Estar')], ['treino', N_('Treino do dia feito (Bem-Estar)')],
  ['cuidados', N_('Cuidados pessoais do dia feitos (Bem-Estar)')], ['refeicoes', N_('Refeições do cronograma feitas (Bem-Estar)')]];

const diasEntre = (a: string, b: string) => Math.round((parseYmd(b).getTime() - parseYmd(a).getTime()) / 864e5);

/** O hábito está planejado para esta data? ("X vezes por semana" vale em qualquer dia.) */
export function planejado(f: FreqHabito, data: string) {
  switch (f.tipo) {
    case 'dia': return true;
    case 'dias': return f.dias.includes(parseYmd(data).getDay());
    case 'vezes': return true;
    case 'cada': return data >= f.desde && diasEntre(f.desde, data) % Math.max(1, f.n) === 0;
  }
}
export function textoFreq(f: FreqHabito) {
  switch (f.tipo) {
    case 'dia': return t('Todo dia');
    case 'dias': return f.dias.length === 7 ? t('Todo dia') : [...f.dias].sort().map(d => t(SEMANA_CURTA[d])).join(', ');
    case 'vezes': return t('{n} vez(es) por semana', { n: f.n });
    case 'cada': return t('A cada {n} dias', { n: f.n });
  }
}

/** O Bem-Estar já registrou este hábito no dia? (água, treino, cuidados, refeições) */
export function feitoNoBemEstar(be: LigacaoBE | null | undefined, data: string, docs: Docs) {
  if (!be) return false;
  const b = docs.bemestar;
  if (be === 'agua') {
    const meta = b.metaMl ?? (b.metaAgua || 8) * (b.copoMl ?? 250);
    return totalAgua(b, data) >= meta;
  }
  if (be === 'treino') return Object.keys(b.feitosEx ?? {}).includes(data) && (b.feitosEx?.[data]?.length ?? 0) > 0;
  const feitos = b.feitos[data] ?? [];
  if (be === 'cuidados') { const cs = doDia(b.cuidados, data); return cs.length > 0 && cs.every(c => feitos.includes(c.id)); }
  const rs = doDia(refeicoesEmOrdem(b), data);
  return rs.length > 0 && rs.every(r => feitos.includes(r.id));
}

/** Datas em que a tarefa ligada a este item (enviada ao My Planner) foi concluída. */
export function feitosNoPlanner(tarefa: string | undefined, docs: Docs): string[] {
  if (!tarefa) return [];
  return docs.planner.tasks.find(x => x.id === tarefa)?.done ?? [];
}

export function feitoEm(h: Habito, data: string, docs: Docs, tarefa?: string) {
  return h.feitos.includes(data) || feitoNoBemEstar(h.be, data, docs) || feitosNoPlanner(tarefa, docs).includes(data);
}

/** Dias seguidos cumpridos (o dia de hoje ainda não feito não quebra a sequência). */
export function seguidos(h: Habito, docs: Docs, tarefa?: string) {
  let n = 0, d = today();
  if (!feitoEm(h, d, docs, tarefa)) d = addDays(d, -1);
  for (let i = 0; i < 1000; i++) {
    if (h.freq.tipo !== 'vezes' && !planejado(h.freq, d)) { d = addDays(d, -1); continue; }
    if (!feitoEm(h, d, docs, tarefa)) break;
    n++; d = addDays(d, -1);
    if (d < h.criado && !h.feitos.some(x => x <= d)) break;
  }
  return n;
}
/** Dias cumpridos no mês (até hoje). */
export function cumpridosNoMes(h: Habito, docs: Docs, tarefa?: string, ref = today()) {
  const ini = ref.slice(0, 8) + '01';
  let n = 0;
  for (let d = ini; d <= ref; d = addDays(d, 1)) if (feitoEm(h, d, docs, tarefa)) n++;
  return n;
}
/** Planejados e cumpridos num período (para os relatórios). */
export function noPeriodo(h: Habito, de: string, ate: string, docs: Docs, tarefa?: string) {
  let plan = 0, feitos = 0;
  const ini = de < h.criado ? h.criado : de;
  for (let d = ini; d <= ate; d = addDays(d, 1)) {
    if (planejado(h.freq, d) && h.freq.tipo !== 'vezes') plan++;
    if (feitoEm(h, d, docs, tarefa)) feitos++;
  }
  if (h.freq.tipo === 'vezes') plan = Math.max(1, Math.round((Math.max(1, diasEntre(ini, ate) + 1) / 7) * h.freq.n));
  return { plan, feitos: Math.min(feitos, Math.max(plan, feitos)) };
}

/** Progresso da meta (0 a 1): passos marcados e cumprimento dos hábitos ligados nos últimos 30 dias. */
export function progressoMeta(m: MetaVida, habitos: Habito[], docs: Docs, tarefaDe: (id: string) => string | undefined) {
  if (m.concluida) return 1;
  const partes: number[] = [];
  if (m.passos.length) partes.push(m.passos.filter(p => p.done).length / m.passos.length);
  const hs = habitos.filter(h => m.habitos.includes(h.id) || h.meta === m.id);
  for (const h of hs) {
    if (h.formado) { partes.push(1); continue; }
    const r = noPeriodo(h, addDays(today(), -29), today(), docs, tarefaDe(h.id));
    partes.push(r.plan ? Math.min(1, r.feitos / r.plan) : 0);
  }
  return partes.length ? partes.reduce((s, x) => s + x, 0) / partes.length : 0;
}
